import { z } from 'zod';

import { stripOfferMarkers } from '../offer-marker';
import { resolveGroundedOffer } from './artifact-grounding';
import {
  entityTypeAnchorFields,
  readEntityTypeAnchor,
} from './entity-type-anchor';
import {
  offerPageOrCustomBothResult,
  offerPageOrCustomBuildOnlyResult,
  offerPageOrCustomFiltersUnresolvedResult,
  turnAbortedNoOpResult,
} from './tool-result-copy';
import type { ClientToolDefinition } from './types';

const schema = z
  .object({
    optionId: z.string(),
    prompt: z.string(),
    ...entityTypeAnchorFields,
    narration: z.string().optional(),
    filterArtifactId: z.string().optional(),
  })
  .passthrough();

/**
 * Partial-fit "offer both": present the templated PAGE first (primary
 * offer), then a secondary build-a-custom-dashboard chip. Order matters —
 * the page is the primary answer, the custom build is the deeper fallback.
 */
export const offerPageOrCustomTool: ClientToolDefinition<typeof schema> = {
  schema,
  // Param-narration offer — see navigate's narrationPolicy note.
  narrationPolicy: 'drop',
  async run(input, { artifacts, deps, emit, signal }) {
    const { optionId, prompt, narration, filterArtifactId } = input;
    const entityType = readEntityTypeAnchor(input);
    // DATA-GROUNDED offer: the page link applies the resolved slice on the
    // destination — "hiring patterns at Ford" must land filtered to Ford, never
    // on the page's default primary (observed live 2026-07-08: Role Sentiment
    // landing on the default role). Filters that resolved nothing would open
    // the page on its defaults, so then only the build offer stands.
    const {
      grounded: groundedFilters,
      filtersUnresolved: offerFiltersUnresolved,
    } = resolveGroundedOffer(artifacts, filterArtifactId);
    // Page chip FIRST, BEFORE the narration renders, so an unresolved page id
    // downgrades the narration's nav marker instead of leaving a dead
    // `[phrase](offer:nav)` trigger with no backing chip. withContinuation is
    // false because this is a terminal "land me on the page" action: replaying
    // the (often causal) prompt there would drive a search/apply filter loop,
    // and the build chip owns the deeper analysis.
    const navResult = offerFiltersUnresolved
      ? undefined
      : deps.requestNavigateConfirm({
          optionId,
          withContinuation: false,
          ...(groundedFilters !== undefined
            ? { resolvedFilters: groundedFilters, entityType }
            : {}),
        });
    if (!navResult || 'unknownPage' in navResult) {
      // No page link: its filters resolved nothing, or the nav id did not
      // resolve (should not happen — optionId is enum-bound to candidate ids).
      // Only the build offer stands. Render the narration with its nav marker
      // downgraded to plain text, then still offer the build so the turn isn't
      // a dead end.
      const buildResult = await deps.requestCreateCustomPage(
        {
          prompt,
          entityType,
          forceOffer: true,
          ...(groundedFilters !== undefined ? { groundedFilters } : {}),
        },
        emit
      );
      if (signal?.aborted) {
        return turnAbortedNoOpResult;
      }
      if (narration) {
        deps.appendAssistantText(
          stripOfferMarkers(narration, 'nav'),
          'offered' in buildResult
            ? { build: buildResult.messageId }
            : undefined
        );
      }
      return offerFiltersUnresolved
        ? offerPageOrCustomFiltersUnresolvedResult
        : offerPageOrCustomBuildOnlyResult;
    }
    // Secondary: the build-a-dashboard chip. forceOffer:true because this tool
    // is a pure OFFER and must NEVER build — the runner hard-routes it to the
    // OFFER path (clearing any stale pending-build ref), so this always
    // resolves `{ offered: true }`. The grounded artifact rides along as the
    // lazy build's landing-filter fallback.
    const buildResult = await deps.requestCreateCustomPage(
      {
        prompt,
        entityType,
        forceOffer: true,
        ...(groundedFilters !== undefined ? { groundedFilters } : {}),
      },
      emit
    );
    // Re-check after the LAST await: the narration append below writes to the
    // transcript, and an engine-unmount / new-turn abort has no rewind (see
    // ClientToolContext.signal).
    if (signal?.aborted) {
      return turnAbortedNoOpResult;
    }
    // Narration LAST so both chips exist to stamp. Both markers are schema-
    // required, so both chips end up suppressed and the inline triggers are the
    // only visible affordances.
    if (narration) {
      deps.appendAssistantText(narration, {
        nav: navResult.messageId,
        ...('offered' in buildResult ? { build: buildResult.messageId } : {}),
      });
    }
    return offerPageOrCustomBothResult(navResult.pageName);
  },
};
